import { Box } from '@chakra-ui/react';
import { Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { useMediaQuery } from 'usehooks-ts';

import HubSessionBanner, {
  HUB_SESSION_BANNER_HEIGHT,
} from '@/client/components/shared/messaging/HubSessionBanner';
import LogoutTimerModal from '@/client/components/overlay/LogoutTimerModal';
import RouteLoading from '@/client/components/media/RouteLoading';
import ViewOnDesktop from '@/client/components/admin/ViewOnDesktop';
import { useAuthenticatedSession } from '@/client/services/hooks/auth/useAuthenticatedSession';
import { useIsActiveHubSession } from '@/client/services/state/hubSessionStore';

/**
 * Authenticated layout with no MainNav / SideNav / Eddy: the full-screen
 * editing surfaces (the academies site builder first). Sibling of
 * LoggedInLayout in the router; shares its session behavior through
 * useAuthenticatedSession so idle logout and the /me refresh still apply.
 *
 * Same desktop gate as DesktopOnlyLayout, but the route is unmounted instead
 * of painted under the overlay: children here size themselves to 100vh, so
 * rendering both would leave the overlay one viewport scroll above the editor.
 */
export default function FullScreenAdminLayout() {
  const { authtoken, logoutTimer } = useAuthenticatedSession();
  const isHubSession = useIsActiveHubSession();
  const isMobileOrTablet = useMediaQuery('(max-width: 1100px)');

  if (!authtoken) return null;

  return (
    <Box position="relative" minH="100vh">
      <HubSessionBanner />

      <ViewOnDesktop show={isMobileOrTablet} offsetForNav={false} />

      {/* The banner is fixed at top:0; children own the viewport below it and
          subtract HUB_SESSION_BANNER_CSS_VAR from their own 100vh calcs */}
      <Box
        paddingTop={isHubSession ? HUB_SESSION_BANNER_HEIGHT : 0}
        data-testid="full-screen-admin-content"
      >
        {!isMobileOrTablet && (
          <Suspense fallback={<RouteLoading />}>
            <Outlet />
          </Suspense>
        )}
      </Box>

      <LogoutTimerModal
        activate={logoutTimer.activate}
        getRemainingTime={logoutTimer.getRemainingTime}
        modalOpen={logoutTimer.modalOpen}
        setModalOpen={logoutTimer.setModalOpen}
      />
    </Box>
  );
}
